/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { get } from 'utils/lodash'

import type { GridColumnVisibilityModel, GridDensity } from '@mui/x-data-grid-pro'

// List-view display state that persists across sessions (in the redux-persist
// whitelist in src/providers/store.js). The List view shares sort with Board V2
// (persistentBoardV2ViewSlice); view mode, stop-grid density and stop-column
// visibility are List-only and live here. Density + column visibility are shared by
// every route's stop grid, so a choice in one persists everywhere and survives the
// grid unmounting on collapse.

// 'datagrid' renders the chip/badge stop cells; 'table' renders the same columns
// as plain text (an Excel-like read), toggled from the List toolbar.
export type ListViewMode = 'datagrid' | 'table'

export type PersistentListViewState = {
  viewMode: ListViewMode
  density: GridDensity
  // null → fall back to the board card-toggle defaults; set once the user edits the
  // native Columns panel.
  columnVisibility: GridColumnVisibilityModel | null
}

const initialState: PersistentListViewState = {
  viewMode: 'datagrid',
  density: 'compact',
  columnVisibility: null,
}

const persistentListViewSlice = createSlice({
  name: 'persistentListView',
  initialState: { ...initialState },
  reducers: {
    setListViewMode: (state, action: PayloadAction<ListViewMode>) => {
      state.viewMode = action.payload
    },
    setListDensity: (state, action: PayloadAction<GridDensity>) => {
      state.density = action.payload
    },
    setListColumnVisibility: (state, action: PayloadAction<GridColumnVisibilityModel>) => {
      state.columnVisibility = action.payload
    },
  },
})

export const { setListViewMode, setListDensity, setListColumnVisibility } = persistentListViewSlice.actions

export const selectListViewMode = (state: unknown): ListViewMode =>
  (get(state, 'persistentListView.viewMode') as ListViewMode) ?? initialState.viewMode

export const selectListDensity = (state: unknown): GridDensity =>
  (get(state, 'persistentListView.density') as GridDensity) ?? initialState.density

export const selectListColumnVisibility = (state: unknown): GridColumnVisibilityModel | null =>
  (get(state, 'persistentListView.columnVisibility') as GridColumnVisibilityModel | null) ?? initialState.columnVisibility

export default persistentListViewSlice.reducer
